/**
 * Professional email: the client side of the portal's mailbox, alias and forwarding API (PHP-3304), plus the wording
 * the Email screens use.
 *
 * Every change is a request with an `Idempotency-Key`: the same key twice has one effect at the email provider. One key
 * belongs to one user attempt (`Attempt`): it is kept while the answer is unknown (no reply, a server error), so a
 * retry cannot do the work twice, and replaced as soon as the server has answered, so the next try is a new request.
 */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { authedPortal } from "./api";
import { ApiError } from "./http";

// ── types (backend/app/modules/mailboxes/schemas.py) ─────────────────────────
export type EmailKind = "mailbox" | "alias" | "forward";
export type EmailState =
  "not_provisioned" | "provisioning" | "unknown" | "active" | "suspended" | "failed" | "deleting" | "deleted";
export type EmailAction = "create" | "update" | "suspend" | "resume" | "reset" | "recover" | "delete";
export type OperationState = "pending" | "successful" | "failed" | "unknown" | "retrying" | "reconciled";

export interface EmailCapability {
  allowed: boolean;
  /** Why not, in the customer's words: shown beside the disabled button. */
  reason: string | null;
}

export interface EmailOperation {
  /** "XOP-…": what support asks for. */
  reference: string;
  state: OperationState;
  confirmed_at: string | null;
  updated_at: string;
}

export interface EmailItem {
  id: string;
  kind: EmailKind;
  address: string | null;
  display_name: string | null;
  domain_id: string;
  fqdn: string;
  /** The mailbox an alias delivers to, or a forwarding rule belongs to. */
  mailbox_id: string | null;
  target: string | null;
  state: EmailState;
  confirmed_state: EmailState | null;
  last_confirmed_at: string | null;
  stale: boolean;
  pending_action: EmailAction | null;
  dns_ready: boolean | null;
  problem: string | null;
  next_action: string | null;
  recoverable_until: string | null;
  can_manage: boolean;
  actions: Partial<Record<EmailAction, EmailCapability>>;
  operation: EmailOperation | null;
  created_at: string;
  updated_at: string;
}

export interface EmailAvailability {
  available: boolean;
  /** A test stand-in that moves no real mail. */
  sandbox: boolean;
  plan_name: string;
  mailbox_limit: number;
  mailboxes_used: number;
  can_create: boolean;
  create_blocked_reason: string | null;
  alias_limit: number;
  forward_limit: number;
  recovery_window_days: number;
}

export interface EmailOverview {
  availability: EmailAvailability;
  items: EmailItem[];
}

export type EmailOutcome = "succeeded" | "failed" | "unknown" | "pending";

export interface EmailActionResult {
  outcome: EmailOutcome;
  /** Whether asking again is safe and may help. */
  retryable: boolean;
  message: string;
  item: EmailItem;
}

// ── API ─────────────────────────────────────────────────────────────────────
const enc = encodeURIComponent;
const base = (orgId: string) => `/v1/organizations/${enc(orgId)}/email`;
const keyed = (key: string) => ({ "Idempotency-Key": key });

export const emailApi = {
  overview: (orgId: string) => authedPortal<EmailOverview>(base(orgId)),
  get: (orgId: string, id: string) => authedPortal<EmailItem>(`${base(orgId)}/${enc(id)}`),
  refresh: (orgId: string, id: string, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/${enc(id)}/refresh`, {
      method: "POST",
      headers: keyed(key),
    }),

  createMailbox: (
    orgId: string,
    body: { domain_id: string; local_part: string; display_name?: string | null },
    key: string,
  ) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/mailboxes`, {
      method: "POST",
      body,
      headers: keyed(key),
    }),
  renameMailbox: (orgId: string, id: string, display_name: string, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/mailboxes/${enc(id)}`, {
      method: "PATCH",
      body: { display_name },
      headers: keyed(key),
    }),

  createAlias: (orgId: string, body: { mailbox_id: string; local_part: string }, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/aliases`, { method: "POST", body, headers: keyed(key) }),
  moveAlias: (orgId: string, id: string, mailbox_id: string, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/aliases/${enc(id)}`, {
      method: "PATCH",
      body: { mailbox_id },
      headers: keyed(key),
    }),

  createForward: (orgId: string, body: { mailbox_id: string; target: string }, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/forwards`, { method: "POST", body, headers: keyed(key) }),
  changeForward: (orgId: string, id: string, target: string, key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/forwards/${enc(id)}`, {
      method: "PATCH",
      body: { target },
      headers: keyed(key),
    }),

  /** Suspend, resume, reset the password of, or recover one item. */
  act: (orgId: string, id: string, action: "suspend" | "resume" | "reset" | "recover", key: string) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/${enc(id)}/${action}`, {
      method: "POST",
      headers: keyed(key),
    }),
  /** Deleting a mailbox also removes its aliases and rules: without `confirmDependents` the answer is a 409. */
  remove: (orgId: string, id: string, key: string, confirmDependents = false) =>
    authedPortal<EmailActionResult>(`${base(orgId)}/${enc(id)}`, {
      method: "DELETE",
      headers: keyed(key),
      query: confirmDependents ? { confirm_dependents: "true" } : undefined,
    }),
};

export const mailboxKeys = {
  overview: (orgId: string) => ["mailboxes", orgId, "overview"] as const,
};

export function useMailboxOverview() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: mailboxKeys.overview(orgId), queryFn: () => emailApi.overview(orgId) });
}

// ── one key per attempt ─────────────────────────────────────────────────────
function newKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Whether the server's answer ends the attempt. Any answer it gave does (a success, or a refusal of the request: the
 * next try is a different request and needs its own key, and the same key with other details would be refused as
 * `IDEMPOTENCY_KEY_REUSED`). No answer at all or a server error does not: the change may have been made, and asking
 * again with the same key returns what the first request did instead of doing it twice.
 */
export function attemptEnded(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status >= 400 && error.status < 500;
}

/** The `Idempotency-Key` of one thing the person is doing: stable across retries, replaced once it has been answered. */
export class Attempt {
  private current: string | null = null;

  /** The key to send now. */
  key(): string {
    this.current ??= newKey();
    return this.current;
  }

  /** The request has been answered (no error), or failed with `error`: keep the key only if the answer is unknown. */
  finished(error?: unknown): void {
    if (error === undefined || attemptEnded(error)) this.current = null;
  }
}

// ── wording ─────────────────────────────────────────────────────────────────
type PillTone = "success" | "danger" | "warning" | "neutral" | "info";

/** The status pill: plain words, never the provider's. */
export function statusPill(item: Pick<EmailItem, "state" | "kind" | "pending_action">): {
  label: string;
  tone: PillTone;
} {
  switch (item.state) {
    case "active":
      return { label: "Active", tone: "success" };
    case "suspended":
      return { label: "Suspended", tone: "warning" };
    case "provisioning":
      return { label: "Setting up", tone: "info" };
    case "unknown":
      return { label: "Status unknown", tone: "warning" };
    case "failed":
      return { label: "Setup failed", tone: "danger" };
    case "deleting":
      return { label: "Deleting", tone: "neutral" };
    case "deleted":
      return { label: "Deleted", tone: "neutral" };
    case "not_provisioned":
      return { label: "Not set up", tone: "neutral" };
  }
}

/** Whether what's shown needs the person's attention: a change we could not confirm, or one that failed. */
export function needsAttention(item: Pick<EmailItem, "state" | "problem">): boolean {
  return item.state === "unknown" || item.state === "failed" || item.problem !== null;
}

/** The label of the button that asks the provider again, by where the item stands. */
export function refreshLabel(item: Pick<EmailItem, "state">): string {
  return item.state === "failed" ? "Try again" : "Refresh status";
}

/** What the person sees for a problem: the server's own words (they say what to do), with a few overrides. */
const REASON_COPY: Record<string, string> = {
  IDEMPOTENCY_KEY_REUSED: "That request was already used for something else. Close this and try again.",
  IDEMPOTENCY_KEY_REQUIRED: "Reload the page and try again.",
  EMAIL_PROVIDER_UNAVAILABLE: "Professional email isn't available right now. Nothing was changed.",
  EMAIL_RESOURCE_CHANGED: "Someone else changed this while you were working on it. Reload and try again.",
  RECOVERY_WINDOW_ENDED: "The recovery window for this mailbox has ended. Create a new mailbox instead.",
};

export function emailProblem(error: unknown): string {
  if (error instanceof ApiError) return REASON_COPY[error.reason] ?? error.message;
  return "Something went wrong. Nothing was changed; try again.";
}

/** A problem the person fixes by changing their plan, not the form. */
export function isPlanProblem(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.reason === "PLAN_LIMIT_REACHED" || error.reason === "EMAIL_READ_ONLY")
  );
}

/** A refusal that is fixed on the Email page: the domain's mail DNS records are not verified yet. */
export function isDnsProblem(error: unknown): boolean {
  return error instanceof ApiError && error.reason === "DNS_NOT_VERIFIED";
}

/** The form field a refusal belongs to, from its reason (the server also sends `fieldErrors`; this covers a refusal sent without one). */
const FIELD_OF_REASON: Record<string, string> = {
  ADDRESS_REQUIRED: "local_part",
  ADDRESS_INVALID: "local_part",
  ADDRESS_RESERVED: "local_part",
  ADDRESS_UNAVAILABLE: "local_part",
  ALIAS_IS_MAILBOX: "local_part",
  NAME_INVALID: "display_name",
  TARGET_INVALID: "target",
  TARGET_REQUIRED: "target",
  FORWARD_TO_SELF: "target",
  FORWARD_LOOP: "target",
  FORWARD_EXISTS: "target",
};

export function fieldOfReason(error: unknown): string | undefined {
  return error instanceof ApiError ? FIELD_OF_REASON[error.reason] : undefined;
}

/**
 * The same error with its message on the field it belongs to when the server sent none (so the shared `<Form>` puts it
 * beside that field instead of only in an alert).
 */
export function withFieldError(error: unknown): unknown {
  if (!(error instanceof ApiError) || error.fieldErrors.length) return error;
  const field = fieldOfReason(error);
  if (!field) return error;
  return new ApiError({
    status: error.status,
    reason: error.reason,
    detail: error.message,
    fieldErrors: [{ field, message: error.message }],
    requestId: error.requestId,
    details: error.details,
  });
}

/**
 * Whether the form already shows this refusal beside a field (the shared `<Form>` also lists it in its summary), so the
 * dialog must not show the same sentence a third time in an alert.
 */
export function shownOnAField(error: unknown): boolean {
  return error instanceof ApiError && (error.fieldErrors.length > 0 || fieldOfReason(error) !== undefined);
}

/** What would also be deleted with a mailbox, from a `MAILBOX_HAS_DEPENDENTS` refusal. */
export function dependents(error: unknown): { aliases: number; forwards: number } | null {
  if (!(error instanceof ApiError) || error.reason !== "MAILBOX_HAS_DEPENDENTS") return null;
  const aliases = Number(error.details.aliases ?? 0);
  const forwards = Number(error.details.forwards ?? 0);
  return { aliases, forwards };
}

/** The part of an address before the `@`, as the server accepts it (letters, numbers, single `.` `_` `+` `-` between). */
export const LOCAL_PART_RE = /^[A-Za-z0-9]+(?:[._+-][A-Za-z0-9]+)*$/;
export const LOCAL_PART_MAX = 64;
export const LOCAL_PART_HELP =
  "Use letters, numbers and single dots, dashes, underscores or plus signs, starting and ending with a letter or number.";

/** The items that belong to a mailbox. */
export function childrenOf(items: readonly EmailItem[], mailboxId: string): EmailItem[] {
  return items.filter((i) => i.mailbox_id === mailboxId && (i.kind === "alias" || i.kind === "forward"));
}
